import axios from 'axios';
import { notification } from 'antd';
import { AUTH_TOKEN, API_URL } from '../configs/AppConfig';
import { RemoveAuthItems } from '../utils/manageAuthTokens';

// Config
const TOKEN_PAYLOAD_KEY = 'token';
const UNAUTHORIZED_STATUS = 401;
const REFRESH_URL = '/api/auth/refresh';
const LOGIN_URL = '/api/auth/login';
const LOGOUT_URL = '/api/auth/logout';

const service = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  withCredentials: true,
});

const refreshAPI = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  withCredentials: true,
});

let refreshPromise = null;
let isLoggingOut = false;

const logout = async () => {
  if (isLoggingOut) return;

  isLoggingOut = true;

  RemoveAuthItems();
  if (window.location.pathname !== '/login') {
    await service
      .post(LOGOUT_URL)
      .then((_res) => {
        notification.error({
          message: 'Session expired',
          description: 'Please login again.',
        });
      })
      .finally(() => {
        window.location.replace('/auth/login');
      });
  } else {
    isLoggingOut = false;
  }
};

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = jwtToken;
    }

    if (config.url.includes('/calculate-impact')) {
      config.timeout = 300000;
    }
    return config;
  },
  (error) => {
    // Do something with request error here
    notification.error({
      message: 'Error',
    });
    Promise.reject(error);
  }
);

// API respone interceptor
service.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || '';

    if (
      status !== UNAUTHORIZED_STATUS ||
      !originalRequest ||
      originalRequest._retry ||
      requestUrl.includes(REFRESH_URL) ||
      requestUrl.includes(LOGIN_URL)
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    let newAccessToken;

    try {
      // Only one refresh request for concurrent 401 responses.
      if (!refreshPromise) {
        refreshPromise = refreshAPI
          .post(REFRESH_URL)
          .then((response) => {
            // Adjust this path to match your actual API response.
            const token = response.data?.token;

            if (!token) {
              console.error(
                'Refresh API returned 200 without the expected token:',
                response.data
              );

              throw new Error('Access token missing from refresh response.');
            }

            localStorage.setItem(AUTH_TOKEN, token);

            return token;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      newAccessToken = await refreshPromise;
    } catch (refreshError) {
      await logout();
      return Promise.reject(refreshError);
    }

    // Retry outside the refresh try/catch.
    // A failed retry should not be mistaken for a refresh failure.
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers[TOKEN_PAYLOAD_KEY] = newAccessToken;

    return service(originalRequest);
  }
);

const isLoginRelatedApi = (config) => {
  const { url } = config;
  return url.includes('/auth');
};
export default service;
